import React from 'react';
import { Link } from 'react-router-dom';

const jobs = [
    {
        title: 'Medical Officer',
        desc: 'Join our team to deliver quality healthcare to communities in need.',
        deadline: 'Apply by: Aug 2024',
    },
    {
        title: 'Community Outreach Lead',
        desc: 'Lead our outreach programs and make a difference in rural health.',
        deadline: 'Apply by: Sep 2024',
    },
    {
        title: 'Telemedicine Nurse',
        desc: 'Provide virtual care and support to patients across the region.',
        deadline: 'Apply by: Oct 2024',
    },
    {
        title: 'IT & Data Security Specialist',
        desc: 'Help secure our telemedicine platform and patient data.',
        deadline: 'Apply by: Oct 2024',
    },
];

const benefits = [
    { icon: <svg className="h-8 w-8 text-malachite-green" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v8m-4-4h8" /></svg>, label: 'Purpose-driven Mission' },
    { icon: <svg className="h-8 w-8 text-lochmara-blue" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>, label: 'Growth & Learning' },
    { icon: <svg className="h-8 w-8 text-brand" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M16 3v4M8 3v4" /></svg>, label: 'Flexible Work Options' },
    { icon: <svg className="h-8 w-8 text-congress-blue" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v8m-4-4h8" /></svg>, label: 'Inclusive Culture' },
];

const Careers = () => (
    <div className="flex min-h-screen w-full flex-col bg-gradient-to-br from-blue-50 to-teal-100">
        {/* Hero Section */}
        <section className="from-brand-light relative flex w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br to-blue-100 py-16 md:py-24">
            <div className="relative z-10 mx-auto max-w-3xl text-center">
                <h1 className="animate-fade-in mb-4 font-ubuntu text-4xl font-bold text-lochmara-blue drop-shadow-lg md:text-5xl">Careers</h1>
                <p className="animate-fade-in mb-6 font-redhat text-xl text-gray-700 delay-200 md:text-2xl">Join a purpose-driven company building the future of healthcare and essential services.</p>
            </div>
            {/* Animated Bubbles and Cross Motif */}
            <svg className="animate-float-slow absolute left-10 top-10 z-0 h-24 w-24 opacity-30" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="40" fill="#0087ca" /></svg>
            <svg className="animate-float-medium absolute right-20 top-24 z-0 h-16 w-16 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="35" fill="#00b937" /></svg>
            <svg className="animate-float-fast absolute bottom-10 left-1/2 z-0 h-20 w-20 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="30" fill="#004487" /></svg>
            <svg className="animate-float-cross absolute bottom-16 right-10 z-0 h-16 w-16" viewBox="0 0 64 64" fill="none"><rect x="28" y="8" width="8" height="48" rx="4" fill="#0087ca" /><rect x="8" y="28" width="48" height="8" rx="4" fill="#00b937" /></svg>
            <style>{`
        @keyframes fade-in { 0% { opacity: 0; transform: translateY(40px);} 100% { opacity: 1; transform: translateY(0);} }
        .animate-fade-in { animation: fade-in 1.2s cubic-bezier(.4,0,.2,1) both; }
        .delay-200 { animation-delay: 0.2s; }
        @keyframes float-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        .animate-float-slow { animation: float-slow 7s ease-in-out infinite; }
        @keyframes float-medium { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-30px); } }
        .animate-float-medium { animation: float-medium 5s ease-in-out infinite; }
        @keyframes float-fast { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-40px); } }
        .animate-float-fast { animation: float-fast 3.5s ease-in-out infinite; }
        @keyframes float-cross { 0%, 100% { transform: translateY(0) rotate(-8deg); } 50% { transform: translateY(-18px) rotate(8deg); } }
        .animate-float-cross { animation: float-cross 6s ease-in-out infinite; }
      `}</style>
        </section>

        {/* Job Openings Grid */}
        <section className="w-full px-4 py-12">
            <div className="mx-auto grid max-w-5xl grid-cols-1 gap-10 sm:grid-cols-2">
                {jobs.map((job, idx) => (
                    <div key={job.title} className="animate-fade-in group flex cursor-pointer flex-col items-center rounded-2xl bg-white/80 p-8 text-center shadow-lg transition-transform duration-300 hover:-translate-y-2 hover:shadow-2xl" style={{ animationDelay: `${0.2 + idx * 0.15}s` }}>
                        <h2 className="mb-2 text-xl font-bold text-lochmara-blue">{job.title}</h2>
                        <p className="mb-4 font-redhat text-gray-700">{job.desc}</p>
                        <span className="mb-4 inline-block w-fit rounded-full bg-lochmara-blue/10 px-4 py-1 text-xs font-bold text-lochmara-blue">{job.deadline}</span>
                        <Link to="/contact" className="mt-auto rounded-full bg-brand px-6 py-2 font-bold text-white shadow transition-transform duration-200 hover:scale-105 hover:bg-brand-green">Apply Now</Link>
                    </div>
                ))}
            </div>
        </section>

        {/* Why Join Us / Benefits Section */}
        <section className="w-full bg-white/80 px-4 py-12">
            <div className="mx-auto max-w-5xl">
                <h2 className="mb-8 text-center font-ubuntu text-2xl font-bold text-congress-blue md:text-3xl">Why Join PureMedify?</h2>
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-4">
                    {benefits.map((b, idx) => (
                        <div key={b.label} className="animate-fade-in flex flex-col items-center rounded-xl bg-white p-6 shadow-lg" style={{ animationDelay: `${0.2 + idx * 0.1}s` }}>
                            <span className="mb-3">{b.icon}</span>
                            <span className="text-center text-lg font-bold text-lochmara-blue">{b.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* Call to Action Section */}
        <section className="flex w-full flex-col items-center bg-gradient-to-br from-blue-100 to-teal-50 px-4 py-12">
            <h2 className="mb-4 text-center font-ubuntu text-2xl font-bold text-congress-blue md:text-3xl">Ready to Make a Difference?</h2>
            <p className="mb-6 max-w-2xl text-center font-redhat text-lg text-gray-700">If you’re passionate about impact, innovation, and community, we want to hear from you. Apply for a role or reach out to join our talent pool!</p>
            <Link to="/contact" className="rounded-full bg-brand px-8 py-4 font-ubuntu text-lg font-semibold text-white shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-brand-green">Contact Us</Link>
        </section>
    </div>
);

export default Careers;
